import { Field, Select, TextArea, TextInput } from './WizardControls';

const triggerLabels = {
  reservation: 'رزرو',
  contract_signing: 'امضای قرارداد',
  fixed_date: 'تاریخ ثابت',
  days_after_contract: 'روز پس از قرارداد',
  months_after_contract: 'ماه پس از قرارداد',
  construction_milestone: 'مرحله ساخت',
  handover: 'تحویل'
};

const blankInstallment = () => ({
  title: '',
  amount_type: 'PERCENTAGE',
  percentage: '',
  fixed_amount: '',
  trigger: 'contract_signing',
  due_date: '',
  offset_value: '',
  milestone: ''
});

const formatMoney = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('fa-IR') : '۰';
};

const unitArea = (unit) => {
  const area = Number(unit.area);
  return Number.isFinite(area) ? area : 0;
};

const calculatedUnitPrice = (unit, pricePerMeter) => {
  const price = Number(pricePerMeter);
  const area = unitArea(unit);
  return Number.isFinite(price) && price > 0 && area > 0 ? price * area : 0;
};

export default function ProjectSalesStep({
  busy,
  projectBundle,
  salesForm,
  selectedProject,
  wizardStatus,
  onApplySalesTemplate,
  onDeleteSalesTemplate,
  onDeleteUnitSalesStrategy,
  onCreateListings,
  onCreateSalesTemplate,
  onSalesFormChange,
  installments,
  onInstallmentsChange,
  selectedUnitIds,
  onSelectionChange,
  presaleReadyUnitCount = 0,
  showSummary = true
}) {
  const availableSelectedUnitIds = selectedUnitIds.filter((id) => projectBundle.units.some((unit) => unit.id === id));
  const selectedCount = availableSelectedUnitIds.length;
  const selectedUnits = projectBundle.units.filter((unit) => availableSelectedUnitIds.includes(unit.id));
  const selectedTotalPrice = projectBundle.units
    .filter((unit) => availableSelectedUnitIds.includes(unit.id))
    .reduce((sum, unit) => sum + calculatedUnitPrice(unit, salesForm.price), 0);

  const toggleUnit = (unitId) => {
    onSelectionChange(selectedUnitIds.includes(unitId)
      ? selectedUnitIds.filter((id) => id !== unitId)
      : [...selectedUnitIds, unitId]);
  };

  const selectAll = () => onSelectionChange(projectBundle.units.map((unit) => unit.id));
  const clearSelection = () => onSelectionChange([]);

  const updateInstallment = (index, key, value) => {
    onInstallmentsChange(installments.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };

  const addInstallment = () => onInstallmentsChange([...installments, blankInstallment()]);
  const removeInstallment = (index) => onInstallmentsChange(installments.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div className="dev-step-layout dev-sales-step">
      {showSummary && (
        <div className="dev-panel dev-step-main dev-step-full">
          <div className="dev-panel-head">
            <div>
              <h2>الگوهای فروش و آگهی پیش فروش</h2>
              <p>الگو را بسازید، قیمت پایه را وارد کنید، شرایط فروش را روی واحدها اعمال کنید و سپس آگهی پیش فروش بسازید.</p>
            </div>
          </div>
          <div className="dev-kpis">
            <div><strong>{projectBundle.salesTemplates.length}</strong><span>الگوهای فروش</span></div>
            <div><strong>{projectBundle.strategies.length}</strong><span>شرایط فروش واحدها</span></div>
            <div><strong>{projectBundle.units.length}</strong><span>واحد قابل اعمال</span></div>
            <div><strong>{wizardStatus?.steps?.presale_listings?.count || 0}</strong><span>آگهی های پیش فروش</span></div>
          </div>
        </div>
      )}
      <form className="dev-panel dev-step-main" onSubmit={(event) => onCreateSalesTemplate(event, installments)}>
        <h2>الگوی فروش</h2>
        <div className="dev-form-stack">
          <Field label="نام الگو"><TextInput value={salesForm.name} onChange={(value) => onSalesFormChange({ ...salesForm, name: value })} /></Field>
          <Field label="ودیعه رزرو"><TextInput type="number" value={salesForm.reservation_fee} onChange={(value) => onSalesFormChange({ ...salesForm, reservation_fee: value })} /></Field>
          <Field label="توضیح"><TextArea value={salesForm.description} onChange={(value) => onSalesFormChange({ ...salesForm, description: value })} /></Field>
          <div className="dev-installments-head">
            <h3>مراحل پرداخت</h3>
            <button type="button" className="dev-btn mini" onClick={addInstallment}>افزودن مرحله</button>
          </div>
          <div className="dev-installments">
            {installments.map((item, index) => (
              <div className="dev-installment-row" key={`${item.sequence || index}-${index}`}>
                <div className="dev-installment-row-head">
                  <strong>مرحله {index + 1}</strong>
                  <button type="button" className="dev-icon-btn" disabled={installments.length === 1} onClick={() => removeInstallment(index)} aria-label={`حذف مرحله ${index + 1}`}>×</button>
                </div>
                <Field label="عنوان"><TextInput value={item.title} onChange={(value) => updateInstallment(index, 'title', value)} /></Field>
                <div className="dev-installment-grid">
                  <Field label="نوع مبلغ">
                    <Select value={item.amount_type} onChange={(value) => updateInstallment(index, 'amount_type', value)}>
                      <option value="PERCENTAGE">درصدی</option>
                      <option value="FIXED">مبلغ ثابت</option>
                    </Select>
                  </Field>
                  {item.amount_type === 'PERCENTAGE'
                    ? <Field label="درصد"><TextInput type="number" min="0" max="100" value={item.percentage} onChange={(value) => updateInstallment(index, 'percentage', value)} /></Field>
                    : <Field label="مبلغ ثابت"><TextInput type="number" min="0" value={item.fixed_amount} onChange={(value) => updateInstallment(index, 'fixed_amount', value)} /></Field>}
                </div>
                <Field label="زمان پرداخت">
                  <Select value={item.trigger} onChange={(value) => updateInstallment(index, 'trigger', value)}>
                    {Object.entries(triggerLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </Select>
                </Field>
                {item.trigger === 'fixed_date' && <Field label="تاریخ سررسید"><TextInput type="date" value={item.due_date} onChange={(value) => updateInstallment(index, 'due_date', value)} /></Field>}
                {['days_after_contract', 'months_after_contract'].includes(item.trigger) && <Field label="مقدار فاصله"><TextInput type="number" min="0" value={item.offset_value} onChange={(value) => updateInstallment(index, 'offset_value', value)} /></Field>}
                {item.trigger === 'construction_milestone' && <Field label="مرحله ساخت"><TextInput value={item.milestone} onChange={(value) => updateInstallment(index, 'milestone', value)} /></Field>}
              </div>
            ))}
          </div>
          <button className="dev-btn secondary" disabled={!selectedProject || busy}>ذخیره الگو</button>
        </div>
        <div className="dev-list compact">
          {projectBundle.salesTemplates.map((template) => (
            <div className="dev-list-row static" key={template.id}>
              <span>{template.name}</span>
              <button type="button" className="dev-btn mini" disabled={!salesForm.price || !selectedCount || busy} onClick={() => onApplySalesTemplate(template.id, availableSelectedUnitIds)}>اعمال روی واحدها</button>
              <button type="button" className="dev-btn danger mini" disabled={busy} onClick={() => onDeleteSalesTemplate(template.id)}>حذف</button>
            </div>
          ))}
        </div>
      </form>
      <div className="dev-panel dev-step-side">
        <h2>اعمال شرایط فروش</h2>
        <Field label="قیمت هر متر مربع"><TextInput type="number" value={salesForm.price} onChange={(value) => onSalesFormChange({ ...salesForm, price: value })} /></Field>
        <div className="dev-selection-toolbar">
          <strong>{selectedCount} انتخاب شده</strong>
          <span>جمع انتخاب: {formatMoney(selectedTotalPrice)} تومان</span>
          <button type="button" className="dev-btn mini" disabled={!projectBundle.units.length} onClick={selectAll}>انتخاب کل پروژه</button>
          <button type="button" className="dev-btn mini" disabled={!selectedCount} onClick={clearSelection}>پاک کردن انتخاب</button>
        </div>
        <p className="dev-muted">انتخاب فاز، بلوک، ساختمان، طبقه و واحد از مدل تعاملی بالای صفحه انجام می‌شود.</p>
        <div className="dev-sales-unit-picker">
          {selectedUnits.map((unit) => (
            <label className={`dev-sales-unit-option ${selectedUnitIds.includes(unit.id) ? 'selected' : ''}`} key={unit.id}>
              <input type="checkbox" checked={selectedUnitIds.includes(unit.id)} onChange={() => toggleUnit(unit.id)} />
              <span>واحد {unit.unit_number}</span>
              <small>{unit.floor ? `طبقه ${unit.floor}` : 'بدون طبقه'} | {unitArea(unit).toLocaleString('fa-IR')} متر | {formatMoney(calculatedUnitPrice(unit, salesForm.price))} تومان</small>
            </label>
          ))}
          {!selectedUnits.length && <p className="dev-muted">هنوز واحدی از مدل پروژه انتخاب نشده است.</p>}
        </div>
        <div className="dev-list compact dev-sales-strategy-list">
          {!!projectBundle.strategies.length && (
            <div className="dev-sales-strategy-head">
              <span>واحد</span>
              <span>قیمت هر متر مربع</span>
              <span>قیمت کل</span>
              <span>عملیات</span>
            </div>
          )}
          {projectBundle.strategies.map((strategy) => {
            const strategyUnit = projectBundle.units.find((unit) => unit.id === strategy.unit);
            return (
              <div className="dev-list-row static dev-sales-strategy-row" key={strategy.id}>
                <span>{strategyUnit ? `واحد ${strategyUnit.unit_number}` : `شرایط #${strategy.id}`}</span>
                <small>{strategy.price_per_meter ? `${formatMoney(strategy.price_per_meter)} تومان` : 'ثبت نشده'}</small>
                <small>{strategy.price ? `${formatMoney(strategy.price)} تومان` : 'ثبت نشده'}</small>
                <button type="button" className="dev-btn danger mini" disabled={busy} onClick={() => onDeleteUnitSalesStrategy(strategy.id)}>حذف</button>
              </div>
            );
          })}
          {!projectBundle.strategies.length && <p className="dev-muted">هنوز شرایط فروشی روی واحدها اعمال نشده است.</p>}
        </div>
      </div>
      <div className="dev-panel dev-step-side">
        <h2>آگهی های پیش فروش</h2>
        <button className="dev-btn primary full" disabled={!selectedProject || !presaleReadyUnitCount || busy} onClick={onCreateListings}>ایجاد پیش نویس آگهی برای همه واحدها</button>
        <p className="dev-muted">{presaleReadyUnitCount} واحد دارای شرایط فروش و مراحل پرداخت برای ایجاد آگهی آماده است.</p>
      </div>
    </div>
  );
}
